import React from 'react';
import { ThresholdLevel, levelToColorClass, levelToBgClass } from '../../utils/thresholds';

export interface MetricField {
  label: string;
  value: string | number;
  unit?: string;
  level?: ThresholdLevel;
  tooltip?: string;
}

interface MetricGroupProps {
  title: string;
  icon?: React.ReactNode;
  level?: ThresholdLevel;        // 整卡告警级别（决定外框颜色）
  primary?: MetricField;          // 顶部大字号主指标
  fields: MetricField[];          // 详细字段列表
  footer?: React.ReactNode;       // 自定义底部内容（per-subscriber 列表用）
}

const FieldRow: React.FC<{ field: MetricField }> = ({ field }) => {
  const colorClass = levelToColorClass(field.level || 'normal');
  return (
    <div className="flex justify-between items-baseline text-[11px] py-1" title={field.tooltip}>
      <span className="text-zinc-400">{field.label}</span>
      <span className={`font-mono font-semibold ${colorClass}`}>
        {field.value}
        {field.unit && <span className="text-[9px] opacity-60 ml-0.5 font-normal">{field.unit}</span>}
      </span>
    </div>
  );
};

const MetricGroup: React.FC<MetricGroupProps> = ({
  title,
  icon,
  level = 'normal',
  primary,
  fields,
  footer,
}) => {
  const bgClass = levelToBgClass(level);

  return (
    <div className={`rounded-2xl border p-4 transition-colors ${bgClass}`}>
      {/* 标题 */}
      <div className="flex items-center gap-2 mb-3">
        {icon && <span className="text-zinc-400">{icon}</span>}
        <h3 className="text-[11px] font-bold tracking-wide text-zinc-300 uppercase">{title}</h3>
      </div>

      {/* 主指标（大字号）*/}
      {primary && (
        <div className="mb-3 pb-3 border-b border-white/5">
          <div className="text-[10px] text-zinc-500 uppercase mb-0.5">{primary.label}</div>
          <div className={`text-2xl font-mono font-bold ${levelToColorClass(primary.level || 'normal')}`}>
            {primary.value}
            {primary.unit && <span className="text-xs opacity-60 ml-1 font-normal">{primary.unit}</span>}
          </div>
        </div>
      )}

      {/* 字段列表 */}
      <div className="space-y-0">
        {fields.map((f, idx) => (
          <FieldRow key={idx} field={f} />
        ))}
      </div>

      {/* 自定义底部 */}
      {footer && <div className="mt-3 pt-3 border-t border-white/5">{footer}</div>}
    </div>
  );
};

export default MetricGroup;
